import { formatCodePoint } from "@/lib/tool-kit/text/code-points";
import { LOOKALIKE_LETTERS, type Script } from "@/lib/tool-kit/text/lookalikes";

/**
 * Find the characters in a text that cannot be seen, or that are not what they look
 * like: zero-width characters, special spaces, bidirectional controls (the "Trojan
 * Source" characters), Unicode tag characters (which can carry a hidden ASCII
 * message), variation selectors, control characters — and Cyrillic or Greek letters
 * hiding inside Latin words.
 *
 * Several of these characters also do real work. A zero-width joiner glues 👩‍💻
 * together, Persian needs the zero-width non-joiner, a flag of England is spelled in tag
 * characters, and VARIATION SELECTOR-16 makes ❤️ red. Those occurrences are still
 * reported but marked `expected`, and cleaning leaves them alone unless asked.
 */

export type HiddenCategory =
  | "zeroWidth"
  | "softHyphen"
  | "blank"
  | "specialSpace"
  | "lineBreak"
  | "bidi"
  | "tag"
  | "variationSelector"
  | "control"
  | "otherFormat"
  | "replacement"
  | "privateUse"
  | "lookalike";

/** The order categories are listed in. */
export const HIDDEN_CATEGORIES: readonly HiddenCategory[] = [
  "zeroWidth",
  "softHyphen",
  "blank",
  "specialSpace",
  "lineBreak",
  "bidi",
  "tag",
  "variationSelector",
  "control",
  "otherFormat",
  "replacement",
  "privateUse",
  "lookalike",
];

/** Why a character is probably doing a legitimate job where it stands. */
export type ExpectedContext = "emoji" | "flag" | "script" | "ideograph";

export interface HiddenCharacter {
  codePoint: number;
  category: HiddenCategory;
  /**
   * The Unicode name, or a code point label such as `<control-0080>`. Null for the
   * long tail of format and private-use characters the detector has no name for.
   */
  name: string | null;
  /** A short tag drawn in place of the character, e.g. "ZWSP". */
  label: string;
  /** What cleaning puts in the character's place. Absent: it is removed. */
  replacement?: string;
}

export interface HiddenFinding extends HiddenCharacter {
  /** UTF-16 offset of the character in the text. */
  index: number;
  /** The character itself. */
  char: string;
  /** Set when the character is doing a legitimate job where it stands. */
  expected: ExpectedContext | null;
}

type NamedEntry = readonly [number, HiddenCategory, string, string];

/** Characters with a fixed description: code point, category, Unicode name, label. */
const NAMED_ENTRIES: readonly NamedEntry[] = [
  [0x00_ad, "softHyphen", "SOFT HYPHEN", "SHY"],
  [0x03_4f, "zeroWidth", "COMBINING GRAPHEME JOINER", "CGJ"],
  [0x06_1c, "bidi", "ARABIC LETTER MARK", "ALM"],
  [0x11_5f, "blank", "HANGUL CHOSEONG FILLER", "HCF"],
  [0x11_60, "blank", "HANGUL JUNGSEONG FILLER", "HJF"],
  [0x17_b4, "blank", "KHMER VOWEL INHERENT AQ", "KVI"],
  [0x17_b5, "blank", "KHMER VOWEL INHERENT AA", "KVI"],
  [0x18_0e, "zeroWidth", "MONGOLIAN VOWEL SEPARATOR", "MVS"],
  [0x20_0b, "zeroWidth", "ZERO WIDTH SPACE", "ZWSP"],
  [0x20_0c, "zeroWidth", "ZERO WIDTH NON-JOINER", "ZWNJ"],
  [0x20_0d, "zeroWidth", "ZERO WIDTH JOINER", "ZWJ"],
  [0x20_0e, "bidi", "LEFT-TO-RIGHT MARK", "LRM"],
  [0x20_0f, "bidi", "RIGHT-TO-LEFT MARK", "RLM"],
  [0x20_2a, "bidi", "LEFT-TO-RIGHT EMBEDDING", "LRE"],
  [0x20_2b, "bidi", "RIGHT-TO-LEFT EMBEDDING", "RLE"],
  [0x20_2c, "bidi", "POP DIRECTIONAL FORMATTING", "PDF"],
  [0x20_2d, "bidi", "LEFT-TO-RIGHT OVERRIDE", "LRO"],
  [0x20_2e, "bidi", "RIGHT-TO-LEFT OVERRIDE", "RLO"],
  [0x20_60, "zeroWidth", "WORD JOINER", "WJ"],
  [0x20_61, "zeroWidth", "FUNCTION APPLICATION", "FA"],
  [0x20_62, "zeroWidth", "INVISIBLE TIMES", "IT"],
  [0x20_63, "zeroWidth", "INVISIBLE SEPARATOR", "IS"],
  [0x20_64, "zeroWidth", "INVISIBLE PLUS", "IP"],
  [0x20_66, "bidi", "LEFT-TO-RIGHT ISOLATE", "LRI"],
  [0x20_67, "bidi", "RIGHT-TO-LEFT ISOLATE", "RLI"],
  [0x20_68, "bidi", "FIRST STRONG ISOLATE", "FSI"],
  [0x20_69, "bidi", "POP DIRECTIONAL ISOLATE", "PDI"],
  [0x28_00, "blank", "BRAILLE PATTERN BLANK", "BRL"],
  [0x31_64, "blank", "HANGUL FILLER", "HF"],
  [0xfe_ff, "zeroWidth", "ZERO WIDTH NO-BREAK SPACE", "BOM"],
  [0xff_a0, "blank", "HALFWIDTH HANGUL FILLER", "HHF"],
  [0xff_fc, "replacement", "OBJECT REPLACEMENT CHARACTER", "OBJ"],
  [0xff_fd, "replacement", "REPLACEMENT CHARACTER", "�"],
  [0x00_a0, "specialSpace", "NO-BREAK SPACE", "NBSP"],
  [0x16_80, "specialSpace", "OGHAM SPACE MARK", "OGHAM SP"],
  [0x20_00, "specialSpace", "EN QUAD", "EN QUAD"],
  [0x20_01, "specialSpace", "EM QUAD", "EM QUAD"],
  [0x20_02, "specialSpace", "EN SPACE", "EN SP"],
  [0x20_03, "specialSpace", "EM SPACE", "EM SP"],
  [0x20_04, "specialSpace", "THREE-PER-EM SPACE", "3/EM SP"],
  [0x20_05, "specialSpace", "FOUR-PER-EM SPACE", "4/EM SP"],
  [0x20_06, "specialSpace", "SIX-PER-EM SPACE", "6/EM SP"],
  [0x20_07, "specialSpace", "FIGURE SPACE", "FIG SP"],
  [0x20_08, "specialSpace", "PUNCTUATION SPACE", "PUNC SP"],
  [0x20_09, "specialSpace", "THIN SPACE", "THIN SP"],
  [0x20_0a, "specialSpace", "HAIR SPACE", "HAIR SP"],
  [0x20_2f, "specialSpace", "NARROW NO-BREAK SPACE", "NNBSP"],
  [0x20_5f, "specialSpace", "MEDIUM MATHEMATICAL SPACE", "MMSP"],
  [0x30_00, "specialSpace", "IDEOGRAPHIC SPACE", "IDEO SP"],
  [0x00_85, "lineBreak", "NEXT LINE", "NEL"],
  [0x20_28, "lineBreak", "LINE SEPARATOR", "LS"],
  [0x20_29, "lineBreak", "PARAGRAPH SEPARATOR", "PS"],
];

/** Cleaning turns special spaces into a space and the rare line breaks into "\n". */
const REPLACEMENT_FOR: Partial<Record<HiddenCategory, string>> = {
  specialSpace: " ",
  lineBreak: "\n",
};

const NAMED: ReadonlyMap<number, HiddenCharacter> = new Map(
  NAMED_ENTRIES.map(([codePoint, category, name, label]) => [
    codePoint,
    {
      codePoint,
      category,
      name,
      label,
      replacement: REPLACEMENT_FOR[category],
    },
  ])
);

/**
 * C0 and C1 control characters: abbreviation and formal name alias (NameAliases.txt).
 * Tab, line feed and carriage return are ordinary text and are not listed; NEXT LINE
 * (U+0085) is reported as a line break above.
 */
const CONTROLS: ReadonlyMap<number, readonly [string, string]> = new Map([
  [0x00, ["NUL", "NULL"]],
  [0x01, ["SOH", "START OF HEADING"]],
  [0x02, ["STX", "START OF TEXT"]],
  [0x03, ["ETX", "END OF TEXT"]],
  [0x04, ["EOT", "END OF TRANSMISSION"]],
  [0x05, ["ENQ", "ENQUIRY"]],
  [0x06, ["ACK", "ACKNOWLEDGE"]],
  [0x07, ["BEL", "ALERT"]],
  [0x08, ["BS", "BACKSPACE"]],
  [0x0b, ["VT", "LINE TABULATION"]],
  [0x0c, ["FF", "FORM FEED"]],
  [0x0e, ["SO", "SHIFT OUT"]],
  [0x0f, ["SI", "SHIFT IN"]],
  [0x10, ["DLE", "DATA LINK ESCAPE"]],
  [0x11, ["DC1", "DEVICE CONTROL ONE"]],
  [0x12, ["DC2", "DEVICE CONTROL TWO"]],
  [0x13, ["DC3", "DEVICE CONTROL THREE"]],
  [0x14, ["DC4", "DEVICE CONTROL FOUR"]],
  [0x15, ["NAK", "NEGATIVE ACKNOWLEDGE"]],
  [0x16, ["SYN", "SYNCHRONOUS IDLE"]],
  [0x17, ["ETB", "END OF TRANSMISSION BLOCK"]],
  [0x18, ["CAN", "CANCEL"]],
  [0x19, ["EOM", "END OF MEDIUM"]],
  [0x1a, ["SUB", "SUBSTITUTE"]],
  [0x1b, ["ESC", "ESCAPE"]],
  [0x1c, ["FS", "INFORMATION SEPARATOR FOUR"]],
  [0x1d, ["GS", "INFORMATION SEPARATOR THREE"]],
  [0x1e, ["RS", "INFORMATION SEPARATOR TWO"]],
  [0x1f, ["US", "INFORMATION SEPARATOR ONE"]],
  [0x7f, ["DEL", "DELETE"]],
  [0x82, ["BPH", "BREAK PERMITTED HERE"]],
  [0x83, ["NBH", "NO BREAK HERE"]],
  [0x84, ["IND", "INDEX"]],
  [0x86, ["SSA", "START OF SELECTED AREA"]],
  [0x87, ["ESA", "END OF SELECTED AREA"]],
  [0x88, ["HTS", "CHARACTER TABULATION SET"]],
  [0x89, ["HTJ", "CHARACTER TABULATION WITH JUSTIFICATION"]],
  [0x8a, ["VTS", "LINE TABULATION SET"]],
  [0x8b, ["PLD", "PARTIAL LINE FORWARD"]],
  [0x8c, ["PLU", "PARTIAL LINE BACKWARD"]],
  [0x8d, ["RI", "REVERSE LINE FEED"]],
  [0x8e, ["SS2", "SINGLE SHIFT TWO"]],
  [0x8f, ["SS3", "SINGLE SHIFT THREE"]],
  [0x90, ["DCS", "DEVICE CONTROL STRING"]],
  [0x91, ["PU1", "PRIVATE USE ONE"]],
  [0x92, ["PU2", "PRIVATE USE TWO"]],
  [0x93, ["STS", "SET TRANSMIT STATE"]],
  [0x94, ["CCH", "CANCEL CHARACTER"]],
  [0x95, ["MW", "MESSAGE WAITING"]],
  [0x96, ["SPA", "START OF GUARDED AREA"]],
  [0x97, ["EPA", "END OF GUARDED AREA"]],
  [0x98, ["SOS", "START OF STRING"]],
  [0x9a, ["SCI", "SINGLE CHARACTER INTRODUCER"]],
  [0x9b, ["CSI", "CONTROL SEQUENCE INTRODUCER"]],
  [0x9c, ["ST", "STRING TERMINATOR"]],
  [0x9d, ["OSC", "OPERATING SYSTEM COMMAND"]],
  [0x9e, ["PM", "PRIVACY MESSAGE"]],
  [0x9f, ["APC", "APPLICATION PROGRAM COMMAND"]],
]);

const ORDINARY_CONTROLS = new Set([0x09, 0x0a, 0x0d]);

/** The Unicode names of the printable ASCII punctuation, for naming tag characters. */
const ASCII_PUNCTUATION_NAMES: Readonly<Record<string, string>> = {
  " ": "SPACE",
  "!": "EXCLAMATION MARK",
  '"': "QUOTATION MARK",
  "#": "NUMBER SIGN",
  $: "DOLLAR SIGN",
  "%": "PERCENT SIGN",
  "&": "AMPERSAND",
  "'": "APOSTROPHE",
  "(": "LEFT PARENTHESIS",
  ")": "RIGHT PARENTHESIS",
  "*": "ASTERISK",
  "+": "PLUS SIGN",
  ",": "COMMA",
  "-": "HYPHEN-MINUS",
  ".": "FULL STOP",
  "/": "SOLIDUS",
  ":": "COLON",
  ";": "SEMICOLON",
  "<": "LESS-THAN SIGN",
  "=": "EQUALS SIGN",
  ">": "GREATER-THAN SIGN",
  "?": "QUESTION MARK",
  "@": "COMMERCIAL AT",
  "[": "LEFT SQUARE BRACKET",
  "\\": "REVERSE SOLIDUS",
  "]": "RIGHT SQUARE BRACKET",
  "^": "CIRCUMFLEX ACCENT",
  _: "LOW LINE",
  "`": "GRAVE ACCENT",
  "{": "LEFT CURLY BRACKET",
  "|": "VERTICAL LINE",
  "}": "RIGHT CURLY BRACKET",
  "~": "TILDE",
};

const DIGIT_NAMES = [
  "ZERO",
  "ONE",
  "TWO",
  "THREE",
  "FOUR",
  "FIVE",
  "SIX",
  "SEVEN",
  "EIGHT",
  "NINE",
];

const ASCII_UPPER = /^[A-Z]$/;
const ASCII_LOWER = /^[a-z]$/;
const ASCII_DIGIT = /^\d$/;

/** The Unicode name of a printable ASCII character, e.g. "LATIN SMALL LETTER H". */
export function asciiCharacterName(character: string): string {
  if (ASCII_UPPER.test(character)) {
    return `LATIN CAPITAL LETTER ${character}`;
  }
  if (ASCII_LOWER.test(character)) {
    return `LATIN SMALL LETTER ${character.toUpperCase()}`;
  }
  if (ASCII_DIGIT.test(character)) {
    return `DIGIT ${DIGIT_NAMES[Number(character)]}`;
  }
  return ASCII_PUNCTUATION_NAMES[character] ?? "";
}

const TAG_BASE = 0xe_00_00;
const TAG_LAST = 0xe_00_7f;
const LANGUAGE_TAG = 0xe_00_01;
const CANCEL_TAG = 0xe_00_7f;
const TAG_PRINTABLE_FIRST = 0xe_00_20;
const TAG_PRINTABLE_LAST = 0xe_00_7e;
const BLACK_FLAG = 0x1_f3_f4;

/** The ASCII character a tag character mirrors, or "" for the non-printing tags. */
export function tagToAscii(codePoint: number): string {
  if (codePoint < TAG_PRINTABLE_FIRST || codePoint > TAG_PRINTABLE_LAST) {
    return "";
  }
  return String.fromCharCode(codePoint - TAG_BASE);
}

function tagCharacter(codePoint: number): HiddenCharacter {
  const ascii = tagToAscii(codePoint);
  let name: string | null = null;
  let label = `TAG ${formatCodePoint(codePoint)}`;
  if (ascii) {
    name = `TAG ${asciiCharacterName(ascii)}`;
    label = `TAG ${ascii}`;
  } else if (codePoint === LANGUAGE_TAG) {
    name = "LANGUAGE TAG";
    label = "TAG LANG";
  } else if (codePoint === CANCEL_TAG) {
    name = "CANCEL TAG";
    label = "TAG END";
  }
  return { codePoint, category: "tag", name, label };
}

const MONGOLIAN_SELECTORS: ReadonlyMap<number, readonly [string, string]> =
  new Map([
    [0x18_0b, ["MONGOLIAN FREE VARIATION SELECTOR ONE", "FVS1"]],
    [0x18_0c, ["MONGOLIAN FREE VARIATION SELECTOR TWO", "FVS2"]],
    [0x18_0d, ["MONGOLIAN FREE VARIATION SELECTOR THREE", "FVS3"]],
    [0x18_0f, ["MONGOLIAN FREE VARIATION SELECTOR FOUR", "FVS4"]],
  ]);

const VS_FIRST = 0xfe_00;
const VS_LAST = 0xfe_0f;
const VS_SUPPLEMENT_FIRST = 0xe_01_00;
const VS_SUPPLEMENT_LAST = 0xe_01_ef;
export const TEXT_STYLE_SELECTOR = 0xfe_0e;
export const EMOJI_STYLE_SELECTOR = 0xfe_0f;

/** VS1–VS256 and the Mongolian free variation selectors. */
function variationSelector(codePoint: number): HiddenCharacter | null {
  let number = 0;
  if (codePoint >= VS_FIRST && codePoint <= VS_LAST) {
    number = codePoint - VS_FIRST + 1;
  } else if (
    codePoint >= VS_SUPPLEMENT_FIRST &&
    codePoint <= VS_SUPPLEMENT_LAST
  ) {
    number = codePoint - VS_SUPPLEMENT_FIRST + 17;
  }
  if (number > 0) {
    return {
      codePoint,
      category: "variationSelector",
      name: `VARIATION SELECTOR-${number}`,
      label: `VS${number}`,
    };
  }
  const mongolian = MONGOLIAN_SELECTORS.get(codePoint);
  return mongolian
    ? {
        codePoint,
        category: "variationSelector",
        name: mongolian[0],
        label: mongolian[1],
      }
    : null;
}

function controlCharacter(codePoint: number): HiddenCharacter | null {
  const isC0 = codePoint < 0x20 || codePoint === 0x7f;
  const isC1 = codePoint >= 0x80 && codePoint <= 0x9f;
  if (!(isC0 || isC1) || ORDINARY_CONTROLS.has(codePoint)) {
    return null;
  }
  const known = CONTROLS.get(codePoint);
  const hex = formatCodePoint(codePoint).slice(2);
  return {
    codePoint,
    category: "control",
    name: known ? known[1] : `<control-${hex}>`,
    label: known ? known[0] : formatCodePoint(codePoint),
  };
}

const SURROGATE_FIRST = 0xd8_00;
const SURROGATE_LAST = 0xdf_ff;
const PRIVATE_USE = /^\p{Co}$/u;
const FORMAT = /^\p{Cf}$/u;

/**
 * What a code point is, when it is one the detector reports — before looking at its
 * neighbours. Look-alike letters depend on the word around them and are found by
 * `findHiddenCharacters` instead.
 */
export function describeHiddenCharacter(
  codePoint: number
): HiddenCharacter | null {
  const named = NAMED.get(codePoint);
  if (named) {
    return named;
  }
  if (codePoint >= TAG_BASE && codePoint <= TAG_LAST) {
    return tagCharacter(codePoint);
  }
  const selector = variationSelector(codePoint);
  if (selector) {
    return selector;
  }
  const control = controlCharacter(codePoint);
  if (control) {
    return control;
  }
  const label = formatCodePoint(codePoint);
  if (codePoint >= SURROGATE_FIRST && codePoint <= SURROGATE_LAST) {
    // Half of a UTF-16 pair with no partner — a character broken in transit.
    return { codePoint, category: "replacement", name: null, label };
  }
  const character = String.fromCodePoint(codePoint);
  if (PRIVATE_USE.test(character)) {
    return { codePoint, category: "privateUse", name: null, label };
  }
  if (FORMAT.test(character)) {
    return { codePoint, category: "otherFormat", name: null, label };
  }
  return null;
}

/* ----------------------------------------------------------------- context */

const ZWNJ = 0x20_0c;
const ZWJ = 0x20_0d;
const KEYCAP = 0x20_e3;
const EMOJI = /^\p{Emoji}$/u;
const PICTOGRAPHIC = /^\p{Extended_Pictographic}$/u;
const EMOJI_MODIFIER = /^\p{Emoji_Modifier}$/u;
const HAN = /^\p{Script=Han}$/u;
const LETTER_OR_MARK = /^[\p{L}\p{M}]$/u;
const EUROPEAN_OR_SHARED =
  /^[\p{Script=Latin}\p{Script=Greek}\p{Script=Cyrillic}\p{Script=Common}\p{Script=Inherited}]$/u;
const BRAILLE_FIRST = 0x28_00;
const BRAILLE_LAST = 0x28_ff;

function charOf(codePoint: number | undefined): string {
  return codePoint === undefined ? "" : String.fromCodePoint(codePoint);
}

/** May end the emoji before a zero-width joiner in a ZWJ sequence such as 👩‍💻. */
function endsEmoji(codePoint: number | undefined): boolean {
  const character = charOf(codePoint);
  return (
    PICTOGRAPHIC.test(character) ||
    EMOJI_MODIFIER.test(character) ||
    codePoint === EMOJI_STYLE_SELECTOR ||
    codePoint === CANCEL_TAG ||
    codePoint === KEYCAP
  );
}

/** A letter or mark of a script that uses joiners: Arabic, the Indic scripts and others. */
function joiningScript(codePoint: number | undefined): boolean {
  const character = charOf(codePoint);
  return LETTER_OR_MARK.test(character) && !EUROPEAN_OR_SHARED.test(character);
}

function isBraille(codePoint: number | undefined): boolean {
  return (
    codePoint !== undefined &&
    codePoint >= BRAILLE_FIRST &&
    codePoint <= BRAILLE_LAST
  );
}

function isIdeographicSelector(codePoint: number): boolean {
  return codePoint >= VS_SUPPLEMENT_FIRST && codePoint <= VS_SUPPLEMENT_LAST;
}

/** Why the character at `k` is legitimate where it stands, if it is. */
function expectedContext(
  points: readonly number[],
  k: number
): ExpectedContext | null {
  const codePoint = points[k];
  const previous = points[k - 1];
  const next = points[k + 1];
  if (
    codePoint === ZWJ &&
    endsEmoji(previous) &&
    PICTOGRAPHIC.test(charOf(next))
  ) {
    return "emoji";
  }
  if (
    (codePoint === ZWJ || codePoint === ZWNJ) &&
    joiningScript(previous) &&
    joiningScript(next)
  ) {
    return "script";
  }
  if (
    (codePoint === TEXT_STYLE_SELECTOR || codePoint === EMOJI_STYLE_SELECTOR) &&
    EMOJI.test(charOf(previous))
  ) {
    return "emoji";
  }
  if (isIdeographicSelector(codePoint) && HAN.test(charOf(previous))) {
    return "ideograph";
  }
  if (codePoint === BRAILLE_FIRST && (isBraille(previous) || isBraille(next))) {
    return "script";
  }
  return null;
}

/**
 * The positions of tag characters that spell a subdivision flag — 🏴 followed by tag
 * letters and CANCEL TAG, as in 🏴󠁧󠁢󠁥󠁮󠁧󠁿 (England). Those tags are the flag, not a hidden
 * message.
 */
function flagTagPositions(points: readonly number[]): Set<number> {
  const positions = new Set<number>();
  for (let k = 0; k < points.length; k++) {
    if (points[k] !== BLACK_FLAG) {
      continue;
    }
    let end = k + 1;
    while (
      end < points.length &&
      points[end] >= TAG_PRINTABLE_FIRST &&
      points[end] <= TAG_PRINTABLE_LAST
    ) {
      end++;
    }
    if (end > k + 1 && points[end] === CANCEL_TAG) {
      for (let tag = k + 1; tag <= end; tag++) {
        positions.add(tag);
      }
      k = end;
    }
  }
  return positions;
}

/* -------------------------------------------------------------- look-alikes */

const WORD = /[\p{L}\p{M}]+/gu;
const LATIN = /^\p{Script=Latin}$/u;
const CYRILLIC = /^\p{Script=Cyrillic}$/u;
const GREEK = /^\p{Script=Greek}$/u;

function scriptOf(character: string): Script | null {
  if (LATIN.test(character)) {
    return "latin";
  }
  if (CYRILLIC.test(character)) {
    return "cyrillic";
  }
  if (GREEK.test(character)) {
    return "greek";
  }
  return null;
}

/** The script most of a word's letters are in; a tie goes to Latin. */
function majorityScript(word: string): Script | null {
  const counts: Record<Script, number> = { latin: 0, cyrillic: 0, greek: 0 };
  for (const character of word) {
    const script = scriptOf(character);
    if (script) {
      counts[script]++;
    }
  }
  const present = (Object.keys(counts) as Script[]).filter(
    (script) => counts[script] > 0
  );
  if (present.length < 2) {
    return null;
  }
  return present.reduce((best, script) =>
    counts[script] > counts[best] ? script : best
  );
}

/**
 * Letters that look like a letter of another script, inside a word written mostly in
 * that other script: the Cyrillic а in "pаypal", or a Latin o slipped into a Russian
 * word. A word written wholly in one script is never flagged, so ordinary Greek and
 * Russian text is left alone.
 */
function findLookalikes(text: string): HiddenFinding[] {
  const findings: HiddenFinding[] = [];
  for (const match of text.matchAll(WORD)) {
    const word = match[0];
    const majority = majorityScript(word);
    if (!majority) {
      continue;
    }
    let offset = match.index;
    for (const character of word) {
      const lookalike = LOOKALIKE_LETTERS.get(character);
      const target = lookalike?.targets[majority];
      if (lookalike && target && lookalike.script !== majority) {
        findings.push({
          codePoint: character.codePointAt(0) ?? 0,
          category: "lookalike",
          name: lookalike.name,
          label: `${character}→${target}`,
          replacement: target,
          index: offset,
          char: character,
          expected: null,
        });
      }
      offset += character.length;
    }
  }
  return findings;
}

/* ------------------------------------------------------------------- finder */

/** Every hidden or look-alike character in `text`, in order. */
export function findHiddenCharacters(text: string): HiddenFinding[] {
  const points: number[] = [];
  const offsets: number[] = [];
  for (let index = 0; index < text.length; ) {
    const codePoint = text.codePointAt(index) ?? 0;
    points.push(codePoint);
    offsets.push(index);
    index += codePoint > 0xff_ff ? 2 : 1;
  }
  const flagTags = flagTagPositions(points);
  const findings: HiddenFinding[] = [];
  for (const [k, codePoint] of points.entries()) {
    const info = describeHiddenCharacter(codePoint);
    if (!info) {
      continue;
    }
    findings.push({
      ...info,
      index: offsets[k],
      char: text.slice(offsets[k], offsets[k] + (codePoint > 0xff_ff ? 2 : 1)),
      expected: flagTags.has(k) ? "flag" : expectedContext(points, k),
    });
  }
  findings.push(...findLookalikes(text));
  return findings.sort((a, b) => a.index - b.index);
}

/* ---------------------------------------------------------------- summaries */

export interface HiddenCharacterRow extends HiddenCharacter {
  char: string;
  count: number;
  /** How many of `count` are doing a legitimate job where they stand. */
  expectedCount: number;
}

/**
 * One row per distinct character, in order of first appearance. Tag characters share
 * one row: a hidden message is one finding to a reader, not a row per letter.
 */
export function groupFindings(
  findings: readonly HiddenFinding[]
): HiddenCharacterRow[] {
  const rows = new Map<string, HiddenCharacterRow>();
  for (const finding of findings) {
    const key =
      finding.category === "tag"
        ? "tag"
        : `${finding.category}:${finding.codePoint}`;
    const row = rows.get(key);
    if (row) {
      row.count++;
      row.expectedCount += finding.expected ? 1 : 0;
      continue;
    }
    rows.set(key, {
      codePoint: finding.codePoint,
      category: finding.category,
      name: finding.name,
      label: finding.label,
      replacement: finding.replacement,
      char: finding.char,
      count: 1,
      expectedCount: finding.expected ? 1 : 0,
    });
  }
  return [...rows.values()];
}

/** Findings per category, counting only the ones not doing a legitimate job. */
export function countSuspicious(
  findings: readonly HiddenFinding[]
): Map<HiddenCategory, number> {
  const counts = new Map<HiddenCategory, number>();
  for (const finding of findings) {
    if (!finding.expected) {
      counts.set(finding.category, (counts.get(finding.category) ?? 0) + 1);
    }
  }
  return counts;
}

/**
 * The ASCII text spelled by tag characters outside flags. Tag characters mirror ASCII
 * one for one (U+E0068 is a hidden "h"), so a run of them is a message no reader can
 * see but a program — or a language model — may still read.
 */
export function hiddenTagText(findings: readonly HiddenFinding[]): string {
  return findings
    .filter((finding) => finding.category === "tag" && !finding.expected)
    .map((finding) => tagToAscii(finding.codePoint))
    .join("");
}

/* ------------------------------------------------------------------ display */

export type DisplaySegment =
  | { kind: "text"; text: string; start: number }
  | { kind: "hidden"; findings: HiddenFinding[]; start: number };

/**
 * The text split into plain runs and hidden characters, for drawing it with every
 * hidden character made visible. Consecutive tag characters become one segment, so a
 * hidden message shows as a single marker rather than one per letter.
 */
export function displaySegments(
  text: string,
  findings: readonly HiddenFinding[]
): DisplaySegment[] {
  const segments: DisplaySegment[] = [];
  let cursor = 0;
  for (const finding of findings) {
    if (finding.index < cursor) {
      continue;
    }
    if (finding.index > cursor) {
      segments.push({
        kind: "text",
        text: text.slice(cursor, finding.index),
        start: cursor,
      });
    }
    const last = segments.at(-1);
    const joinsTagRun =
      finding.category === "tag" &&
      last?.kind === "hidden" &&
      last.findings[0].category === "tag" &&
      Boolean(last.findings[0].expected) === Boolean(finding.expected) &&
      finding.index === cursor;
    if (joinsTagRun && last?.kind === "hidden") {
      last.findings.push(finding);
    } else {
      segments.push({
        kind: "hidden",
        findings: [finding],
        start: finding.index,
      });
    }
    cursor = finding.index + finding.char.length;
  }
  if (cursor < text.length) {
    segments.push({ kind: "text", text: text.slice(cursor), start: cursor });
  }
  return segments;
}

/* ----------------------------------------------------------------- cleaning */

export type CleanupChoice =
  | "zeroWidth"
  | "spaces"
  | "bidi"
  | "tags"
  | "selectors"
  | "controls"
  | "lookalikes"
  | "placeholders";

export const CLEANUP_CHOICES: readonly CleanupChoice[] = [
  "zeroWidth",
  "spaces",
  "bidi",
  "tags",
  "selectors",
  "controls",
  "lookalikes",
  "placeholders",
];

/** The categories each cleanup choice acts on. */
export const CLEANUP_CATEGORIES: Readonly<
  Record<CleanupChoice, readonly HiddenCategory[]>
> = {
  zeroWidth: ["zeroWidth", "softHyphen", "blank", "otherFormat"],
  spaces: ["specialSpace", "lineBreak"],
  bidi: ["bidi"],
  tags: ["tag"],
  selectors: ["variationSelector"],
  controls: ["control"],
  lookalikes: ["lookalike"],
  placeholders: ["replacement", "privateUse"],
};

export const DEFAULT_CLEANUP: Readonly<Record<CleanupChoice, boolean>> = {
  zeroWidth: true,
  spaces: true,
  bidi: true,
  tags: true,
  selectors: true,
  controls: true,
  lookalikes: false,
  placeholders: false,
};

export interface CleanupResult {
  text: string;
  removed: number;
  replaced: number;
}

/**
 * Remove or normalise the chosen kinds of character. Special spaces become ordinary
 * spaces, the rare line separators become line feeds, look-alike letters become the
 * letter they imitate, and everything else is removed. Characters doing a legitimate
 * job (emoji joiners, flag tags, Persian and Indic joiners) are kept unless
 * `includeExpected` is set.
 */
export function cleanHiddenCharacters(
  text: string,
  findings: readonly HiddenFinding[],
  choices: Readonly<Record<CleanupChoice, boolean>>,
  includeExpected: boolean
): CleanupResult {
  const active = new Set<HiddenCategory>(
    CLEANUP_CHOICES.filter((choice) => choices[choice]).flatMap(
      (choice) => CLEANUP_CATEGORIES[choice]
    )
  );
  let output = "";
  let cursor = 0;
  let removed = 0;
  let replaced = 0;
  for (const finding of findings) {
    const acts =
      active.has(finding.category) &&
      (includeExpected || !finding.expected) &&
      finding.index >= cursor;
    if (!acts) {
      continue;
    }
    output += text.slice(cursor, finding.index);
    if (finding.replacement === undefined) {
      removed++;
    } else {
      output += finding.replacement;
      replaced++;
    }
    cursor = finding.index + finding.char.length;
  }
  return { text: output + text.slice(cursor), removed, replaced };
}
